import { useEffect, useRef, useCallback } from 'react'
import { ChatMessage } from '../types'

interface UseInfiniteScrollOptions {
  onLoadMore: () => Promise<void>
  hasMore: boolean
  loading: boolean
  enabled?: boolean
  scrollContainer?: HTMLElement | null
  messages: ChatMessage[]
  animationComplete?: boolean
  // Newer messages support (for deeplinks)
  onLoadMoreNewer?: () => Promise<void>
  hasMoreNewer?: boolean
  isDeeplinked?: boolean
}

export const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  loading,
  enabled = true,
  scrollContainer,
  messages,
  animationComplete = true,
  onLoadMoreNewer,
  hasMoreNewer = false,
  isDeeplinked = false,
}: UseInfiniteScrollOptions) => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const newestObserverRef = useRef<IntersectionObserver | null>(null)
  const oldestMessageRef = useRef<HTMLDivElement>(null)
  const newestMessageRef = useRef<HTMLDivElement>(null)

  const loadMore = useCallback(async () => {
    if (!hasMore || loading || !enabled || !animationComplete) return
    await onLoadMore()
  }, [onLoadMore, hasMore, loading, enabled, animationComplete])

  useEffect(() => {
    if (
      !oldestMessageRef.current ||
      !hasMore ||
      !enabled ||
      messages.length === 0 ||
      !animationComplete
    ) {
      return
    }

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    // Create fresh observer instance
    observerRef.current = new IntersectionObserver(
      entries => {
        const [entry] = entries
        if (
          entry.isIntersecting &&
          enabled &&
          hasMore &&
          !loading &&
          animationComplete
        ) {
          loadMore()
        }
      },
      {
        root: scrollContainer || null,
        threshold: 0,
        rootMargin: '50px 0px',
      },
    )

    observerRef.current.observe(oldestMessageRef.current)
  }, [
    scrollContainer,
    enabled,
    hasMore,
    messages.length,
    animationComplete,
    loadMore,
    loading,
  ])

  // Observer for newest message (forwards scrolling when deeplinked)
  useEffect(() => {
    if (
      !newestMessageRef.current ||
      !hasMoreNewer ||
      !isDeeplinked ||
      !onLoadMoreNewer ||
      messages.length === 0 ||
      !animationComplete
    ) {
      return
    }

    // Clean up existing observer
    if (newestObserverRef.current) {
      newestObserverRef.current.disconnect()
      newestObserverRef.current = null
    }

    // Create fresh observer instance
    newestObserverRef.current = new IntersectionObserver(
      entries => {
        const [entry] = entries
        if (
          entry.isIntersecting &&
          enabled &&
          hasMoreNewer &&
          !loading &&
          animationComplete
        ) {
          onLoadMoreNewer()
        }
      },
      {
        root: scrollContainer || null,
        threshold: 0,
        rootMargin: '50px 0px',
      },
    )

    newestObserverRef.current.observe(newestMessageRef.current)
  }, [
    scrollContainer,
    enabled,
    hasMoreNewer,
    isDeeplinked,
    messages.length,
    animationComplete,
    onLoadMoreNewer,
    loading,
  ])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
      if (newestObserverRef.current) {
        newestObserverRef.current.disconnect()
      }
    }
  }, [])

  return { oldestMessageRef, newestMessageRef }
}
